/**
 * Lien d'évitement vers le contenu principal (audit Impeccable, 2026-09-07).
 *
 * PROBLÈME RÉEL : la `Sidebar` précède `<main>` dans l'ordre du DOM des deux
 * apps et compte jusqu'à ~20 liens. Un utilisateur au clavier (ou au lecteur
 * d'écran) devait donc les traverser TOUS, sur CHAQUE page, avant d'atteindre
 * le contenu. WCAG 2.1 niveau A, critère 2.4.1 Bypass Blocks — l'un des rares
 * critères de niveau A que le produit ne remplissait pas.
 *
 * Composant PARTAGÉ par les deux apps plutôt que dupliqué : les deux ont la
 * même structure `Sidebar` + `<main>` depuis UX-17, et un lien d'évitement qui
 * divergerait entre les portails serait un piège de maintenance pour un
 * élément que personne ne voit au quotidien.
 *
 * VISIBLE UNIQUEMENT AU FOCUS, et jamais masqué par `display: none` ni
 * `visibility: hidden` : ces deux propriétés retirent l'élément de l'ordre de
 * tabulation, ce qui rendrait le lien inatteignable — exactement le bug que ce
 * composant est censé corriger. `sr-only` (Tailwind) le réduit à un pixel
 * découpé hors flux tout en le laissant focusable, et `focus:not-sr-only` le
 * ramène dans le flux visuel dès qu'il reçoit le focus.
 *
 * Couleurs issues du thème partagé (`brand-600`/blanc, voir theme.css), pas
 * une paire inventée pour ce seul composant : quand il apparaît, il doit
 * ressembler au reste du produit.
 */
export interface SkipToContentLinkProps {
  /**
   * Libellé du lien. Requis et non défaulté à un texte français : `web-parent`
   * est bilingue (voir src/i18n) et doit passer sa chaîne traduite, tandis que
   * `web-admin` reste en français en dur. Un défaut ici aurait silencieusement
   * laissé du français dans l'interface anglaise.
   */
  label: string;
  /**
   * Cible du lien, sans le `#`. Doit correspondre à l'`id` porté par `<main>`.
   * Paramétrable plutôt que codé en dur pour que l'`id` reste une décision de
   * l'app qui rend son propre `<main>`.
   */
  targetId?: string;
}

export function SkipToContentLink({ label, targetId = 'contenu-principal' }: SkipToContentLinkProps) {
  return (
    <a
      href={`#${targetId}`}
      className={
        'ss-skip-link sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 ' +
        'focus:rounded-md focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-sm focus:font-medium ' +
        'focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2'
      }
    >
      {label}
    </a>
  );
}
